import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { AudioLines, Loader2, Sparkles, Send, ArrowLeft, Download } from 'lucide-react';

const DEFAULT_INTRO = 'Sehr geehrte Mitglieder des Advisory Committees,\n\nvielen Dank für Ihre Teilnahme am AC-Meeting und den wertvollen Austausch. Nachfolgend finden Sie die Zusammenfassung der besprochenen Deals und Themen.';

const fmtDate = (s) => {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
};

export function ACPostMeetingDialog({ open, onOpenChange, meeting, teamRecipients = [], onSent }) {
  const [step, setStep] = useState('input');
  const [present, setPresent] = useState({});
  const [rawText, setRawText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [recordings, setRecordings] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [loadingNote, setLoadingNote] = useState('');
  const [intro, setIntro] = useState(DEFAULT_INTRO);
  const [general, setGeneral] = useState('');
  const [dealNotes, setDealNotes] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [sending, setSending] = useState(false);
  const [includeTeam, setIncludeTeam] = useState(true);

  useEffect(() => {
    if (open && meeting) setIncludeTeam(!meeting.post_summary);
  }, [open, meeting]);

  useEffect(() => {
    if (!open || !meeting) return;
    const list = meeting.ac_recipients?.length ? meeting.ac_recipients : (meeting.recipients || []);
    const preset = meeting.present?.length ? meeting.present : list;
    const init = {};
    list.forEach(r => { init[r] = preset.includes(r); });
    setPresent(init);
  }, [open, meeting]);

  const reset = () => {
    setStep('input'); setRawText(''); setGeneral(''); setDealNotes([]);
    setUnmatched([]); setRecordings(null); setIntro(DEFAULT_INTRO);
  };
  const handleClose = (o) => { if (!o) reset(); onOpenChange(o); };

  const loadRecordings = async () => {
    setLoadingRecs(true);
    try { const res = await api.getPlaudRecordings(30); setRecordings(res.data); }
    catch (err) { toast.error(err.response?.data?.detail || 'Plaud is not connected'); }
    setLoadingRecs(false);
  };

  const loadNote = async (rec) => {
    setLoadingNote(rec.id);
    try {
      const res = await api.getPlaudRecordingNote(rec.id);
      setRawText(prev => (prev.trim() ? `${prev}\n\n${res.data.text}` : res.data.text));
      toast.success(`"${rec.name}" loaded into the text box`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not load the Plaud note'); }
    setLoadingNote('');
  };

  const handleParse = async () => {
    if (!rawText.trim()) { toast.error('Paste or load the Plaud summary first'); return; }
    setParsing(true);
    try {
      const res = await api.parseAcPlaud(rawText);
      setGeneral(res.data.general_summary || '');
      const parsed = res.data.deal_notes || [];
      const byId = {};
      parsed.forEach(d => { if (d.project_id) byId[d.project_id] = d; });
      const agendaBoxes = (meeting.deals || []).map(d =>
        byId[d.project_id] || { project_id: d.project_id, name: d.project_name, note: '' });
      const extras = parsed.filter(d => !d.project_id || !(meeting.deals || []).some(m => m.project_id === d.project_id));
      setDealNotes([...agendaBoxes, ...extras]);
      setUnmatched(res.data.unmatched || []);
      if (!parsed.length && !(res.data.general_summary || '').trim()) {
        toast.info('No content could be mapped — check the pasted text');
      } else setStep('review');
    } catch (err) { toast.error(err.response?.data?.detail || 'AI analysis failed'); }
    setParsing(false);
  };

  const presentEmails = Object.keys(present).filter(e => present[e]);
  const recipientPreview = [...new Map(
    [...presentEmails, ...(includeTeam ? teamRecipients : [])].map(e => [e.toLowerCase(), e])
  ).values()];

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await api.sendAcPostMeeting({
        intro, general_summary: general, deal_notes: dealNotes,
        present: presentEmails, include_team: includeTeam, force: !!meeting?.post_summary,
      });
      toast.success(`Summary sent to ${res.data.sent_to.length} recipient${res.data.sent_to.length !== 1 ? 's' : ''} & archived under Meeting Summaries`);
      reset(); onOpenChange(false); onSent?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Sending failed'); }
    setSending(false);
  };

  if (!meeting) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[85vh] flex flex-col" data-testid="ac-post-meeting-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
            <AudioLines className="w-5 h-5 text-violet-400" /> AC Post-Meeting
            <span className="text-xs font-normal text-muted-foreground">— {fmtDate(meeting.meeting_date)}</span>
          </DialogTitle>
        </DialogHeader>

        {step === 'input' ? (
          <div className="flex-1 overflow-y-auto space-y-4 mt-1 pr-1">
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1.5">AC members present</p>
              {Object.keys(present).length === 0 ? (
                <p className="text-xs text-amber-300" data-testid="ac-no-recipients-warning">
                  No AC members found — add them as AC recipients in the AC Settings (or invite them as AC users). The email would go to the team only.
                </p>
              ) : Object.keys(present).map(r => (
                <label key={r} className="flex items-center gap-2 py-1 cursor-pointer select-none w-fit" data-testid={`ac-present-${r}`}>
                  <Checkbox checked={!!present[r]}
                    onCheckedChange={(v) => setPresent(prev => ({ ...prev, [r]: !!v }))}
                    className="border-border data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600" />
                  <span className={`text-xs ${present[r] ? 'text-foreground' : 'text-muted-foreground line-through'}`}>{r}</span>
                </label>
              ))}
            </div>

            {(meeting.deals || []).length > 0 && (
              <div>
                <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Deals of this AC meeting</p>
                <div className="flex flex-wrap gap-1.5">
                  {meeting.deals.map(d => (
                    <span key={d.project_id} className="text-[10px] font-mono text-foreground bg-secondary border border-border rounded px-1.5 py-0.5">
                      {d.project_name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Plaud notes (transcript / summary)</p>
                <button onClick={loadRecordings} disabled={loadingRecs} data-testid="ac-plaud-load-recordings"
                  className="text-[11px] text-violet-300 hover:text-violet-200 flex items-center gap-1 disabled:opacity-50">
                  {loadingRecs ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Load from Plaud
                </button>
              </div>
              {recordings !== null && (
                <div className="border border-border rounded-sm mb-2 max-h-36 overflow-y-auto" data-testid="ac-plaud-recordings">
                  {recordings.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-3">No recordings in the last 30 days.</p>
                  ) : recordings.map(rec => (
                    <button key={rec.id} onClick={() => loadNote(rec)} disabled={!!loadingNote}
                      data-testid={`ac-plaud-rec-${rec.id}`}
                      className="w-full flex items-center gap-2 px-3 py-2 border-b border-border last:border-b-0 hover:bg-secondary/30 transition-colors text-left disabled:opacity-50">
                      {loadingNote === rec.id ? <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-400 shrink-0" />
                        : <AudioLines className="w-3.5 h-3.5 text-violet-400 shrink-0" />}
                      <span className="text-xs text-foreground flex-1 min-w-0 truncate">{rec.name}</span>
                      <span className="text-[10px] text-muted-foreground font-mono shrink-0">
                        {rec.created_at ? new Date(rec.created_at).toLocaleDateString('de-DE') : ''}{rec.duration_min ? ` · ${rec.duration_min} min` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <textarea value={rawText} onChange={e => setRawText(e.target.value)}
                placeholder="Paste the Plaud summary / transcript of the AC meeting here..."
                className="w-full h-48 bg-secondary border border-border rounded-sm p-3 text-sm text-foreground resize-y focus:outline-none focus:border-violet-500 font-mono"
                data-testid="ac-plaud-text-input" />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-border sticky bottom-0 bg-card">
              <Button variant="ghost" onClick={() => handleClose(false)} className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
              <Button onClick={handleParse} disabled={parsing || !rawText.trim()} data-testid="ac-plaud-analyze-btn"
                className="bg-violet-600 hover:bg-violet-500 text-white rounded-sm text-xs h-8 px-4">
                {parsing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                {parsing ? 'Analyzing...' : 'Analyze with AI'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4 mt-1 pr-1">
            <p className="text-sm text-muted-foreground">
              Review and edit the summary below. On send, the email goes to the <span className="text-foreground font-medium">present AC members + all team members</span>,
              it is archived under Meeting Summaries and each deal gets an "AC Meeting" note.
            </p>

            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Thank-you intro</p>
              <textarea value={intro} onChange={e => setIntro(e.target.value)} rows={4}
                className="w-full bg-secondary border border-border rounded-sm p-3 text-sm text-foreground resize-y focus:outline-none focus:border-violet-500"
                data-testid="ac-review-intro" />
            </div>

            {dealNotes.length > 0 && (
              <div className="space-y-3">
                <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Deal summaries ({dealNotes.length})</p>
                {dealNotes.map((d, i) => (
                  <div key={d.project_id || `manual-${i}`} className="border border-border rounded-sm p-3 bg-secondary/30">
                    <p className="text-sm font-medium text-foreground mb-1.5">{d.name}
                      {!(d.note || '').trim() && <span className="ml-2 text-[10px] font-mono text-muted-foreground">nicht besprochen / leer</span>}
                    </p>
                    <textarea value={d.note} rows={4}
                      placeholder="Nicht besprochen — leer lassen (wird nicht versendet) oder manuell ergänzen…"
                      onChange={e => setDealNotes(prev => prev.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))}
                      className="w-full bg-secondary border border-border rounded-sm p-2.5 text-sm text-foreground resize-y focus:outline-none focus:border-violet-500"
                      data-testid={`ac-review-note-${d.project_id || i}`} />
                  </div>
                ))}
              </div>
            )}
            {dealNotes.length === 0 && (
              <p className="text-sm text-muted-foreground italic">No AC deals were matched in the text.</p>
            )}

            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">General topics</p>
              <textarea value={general} onChange={e => setGeneral(e.target.value)} rows={4}
                placeholder="No general topics found"
                className="w-full bg-secondary border border-border rounded-sm p-3 text-sm text-foreground resize-y focus:outline-none focus:border-violet-500"
                data-testid="ac-review-general" />
            </div>

            {unmatched.length > 0 && (
              <div className="border border-amber-500/30 bg-amber-500/10 rounded-sm p-3" data-testid="ac-plaud-unmatched">
                <p className="text-xs font-bold text-amber-400 mb-1.5">Discussed but not on the AC deal list:</p>
                {unmatched.map((u, i) => (
                  <div key={i} className="flex items-start gap-2 mb-1.5 last:mb-0">
                    <p className="text-xs text-amber-300/90 whitespace-pre-wrap flex-1 min-w-0">{u}</p>
                    <button data-testid={`ac-unmatched-add-${i}`}
                      onClick={() => {
                        const ci = u.indexOf(':');
                        const nm = (ci > 0 ? u.slice(0, ci) : u.slice(0, 40)).trim();
                        const note = (ci > 0 ? u.slice(ci + 1) : u).trim();
                        setDealNotes(prev => [...prev, { project_id: null, name: nm, note: note.startsWith('–') ? note : `– ${note}` }]);
                        setUnmatched(prev => prev.filter((_, j) => j !== i));
                      }}
                      className="text-[10px] text-amber-200 border border-amber-500/40 hover:bg-amber-500/20 rounded px-1.5 py-0.5 shrink-0">
                      + Add as deal section
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="border border-border rounded-sm p-3 bg-secondary/20" data-testid="ac-recipients-preview">
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Recipients ({recipientPreview.length})</p>
              <p className="text-[11px] text-muted-foreground leading-relaxed">{recipientPreview.join(', ') || '—'}</p>
              <label className="flex items-center gap-2 mt-2 cursor-pointer select-none w-fit" data-testid="ac-include-team-toggle">
                <Checkbox checked={includeTeam} onCheckedChange={(v) => setIncludeTeam(!!v)}
                  className="border-border data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600" />
                <span className="text-[11px] text-muted-foreground">Also send to all team members{meeting.post_summary ? ' (again)' : ''}</span>
              </label>
              {presentEmails.length === 0 && (
                <p className="text-[11px] text-amber-300 mt-1.5" data-testid="ac-no-present-warning">
                  ⚠ No AC members selected as present — this email will only go to the team.
                </p>
              )}
            </div>

            <div className="flex justify-between gap-2 pt-2 border-t border-border sticky bottom-0 bg-card">
              <Button variant="ghost" onClick={() => setStep('input')} data-testid="ac-review-back-btn"
                className="text-muted-foreground rounded-sm text-xs h-8 px-3">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
              <Button onClick={handleSend} disabled={sending || recipientPreview.length === 0} data-testid="ac-post-send-btn"
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-xs h-8 px-4">
                {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                {meeting.post_summary ? 'Send again & archive' : 'Send email & archive'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
